import { getRouteApi } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import Button from '@/components/Button/Button'
import LinkText from '@/components/LinkText/LinkText'
import { type SelectOption } from '@/components/Select/Select'
import Table, { type TableAction } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import ExportButton from '@/components/common/ExportButton'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { useColumns } from '@/hooks/useColumns'
import { usePagination } from '@/hooks/usePagination'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { toast } from '@/hooks/useToast'
import useAuthStore from '@/store/useAuthStore'
import { getColumnWidth } from '@/utils/get-column-width'
import { getFilterValue } from '@/utils/get-filter-value'
import { getValue } from '@/utils/get-value'

import styles from './ClassesView.module.scss'
import RemoveClassModal from '../components/RemoveClassModal'
import type { ClassesFiltersKey } from '../constants/classes-filters'
import useClassesDownload from '../mutations/useClassesDownload'
import { classesQueryOptions } from '../queries/classListFilters/classesQueryOptions'
import { studentsQueryOptions } from '../queries/classListFilters/studentsQueryOptions'
import { tutorsQueryOptions } from '../queries/classListFilters/tutorsQueryOptions'
import useAcademicLevelsOptions from '../queries/classListFilters/useAcademicLevelsOptions'
import useStudentClasses, { type Class } from '../queries/useStudentClasses'
import useStudentClassesCount from '../queries/useStudentClassesCount'

const routeApi = getRouteApi('/_auth/students-and-classes/classes/')

const ClassesView = () => {
  const { t } = useTranslation(['classes'])
  const { page, pageSize, id, academicLevelId, tutorId, studentId } =
    routeApi.useSearch()
  const navigate = routeApi.useNavigate()
  const { user } = useAuthStore()

  const { isMobile, isExtraLarge } = useScreenResolution()

  const [openDeleteClassConfirmModal, setOpenDeleteClassConfirmModal] =
    useState(false)
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null)

  const requestParams = {
    id: id?.map(getValue),
    academicLevelId,
    tutorId: tutorId?.map(getValue),
    studentId: studentId?.map(getValue)
  }

  const {
    data: classes,
    isLoading: isClassesLoading,
    refetch: refetchClassess
  } = useStudentClasses({
    ...requestParams,
    page,
    pageSize
  })

  const { data: classesCount, isLoading: isClassesCountLoading } =
    useStudentClassesCount()

  const { columns, ...manageColumns } = useColumns<Class>(
    [
      {
        key: 'display_name',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.SM),
        label: t('header.class'),
        cellDataTestId: 'class',
        render: (value: Class) => (
          <LinkText
            dataTestId="class-link"
            to="/students-and-classes/classes/$classId/details"
            params={{
              classId: value.id
            }}
          >
            {value.name}
          </LinkText>
        )
      },
      {
        dataIndex: ['academicLevel', 'name'],
        key: 'academic_level',
        cellDataTestId: 'academic-level',
        label: t('header.academic-level'),
        width: isMobile ? TABLE_CELL_WIDTH.LG : undefined
      },

      {
        key: 'tutor',
        cellDataTestId: 'tutor',
        label: t('header.tutor'),
        width: isMobile ? TABLE_CELL_WIDTH.XL : undefined,
        render: (value: Class) =>
          value.tutor ? (
            <LinkText
              to="/users/$userId/details"
              params={{
                userId: value.tutor.id
              }}
            >
              {value.tutor.fullName}
            </LinkText>
          ) : (
            <span className={styles.textUnassigned}>
              {t('text.unassigned')}
            </span>
          )
      },
      {
        dataIndex: 'numberOfStudents',
        key: 'number_of_students',
        cellDataTestId: 'number-of-students',
        label: t('header.number-of-students'),
        width: isMobile ? TABLE_CELL_WIDTH.LG : undefined
      }
    ],
    { routeApi }
  )

  const pagination = usePagination(routeApi)

  const { data: academicLevels, isLoading: isAcademicLevelsLoading } =
    useAcademicLevelsOptions()

  const changeFilter = (
    key: ClassesFiltersKey,
    value?: string | string[] | SelectOption<string>[]
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const handleClearAll = () => {
    navigate({
      search: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
    })
  }

  const filters: Filter[] = [
    {
      label: t('label.class'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'classId',
        dataTestId: 'class-filter',
        multiple: true,
        queryOptions: classesQueryOptions,
        value: id,
        placeholder: t('label.class'),
        onChange: value => changeFilter('id', value)
      }
    },
    {
      label: t('label.academic-level'),
      variant: 'multiselect',
      filterProps: {
        id: 'academic-level',
        dataTestId: 'academic-level-filter',
        multiple: true,
        loading: isAcademicLevelsLoading,
        options: academicLevels || [],
        value: academicLevelId,
        placeholder: t('label.academic-level'),
        onChange: value => changeFilter('academicLevelId', value)
      }
    },
    {
      label: t('label.tutor'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'tutorId',
        dataTestId: 'tutor-filter',
        multiple: true,
        queryOptions: tutorsQueryOptions,
        value: tutorId,
        placeholder: t('label.tutor'),
        onChange: value => changeFilter('tutorId', value)
      }
    },
    {
      label: t('label.student'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'student',
        dataTestId: 'student-filter',
        multiple: true,
        queryOptions: studentsQueryOptions,
        value: studentId,
        placeholder: t('label.student'),
        onChange: value => changeFilter('studentId', value)
      }
    }
  ]
  const actions: TableAction<Class>[] = [
    ...(user?.isSuperAdmin
      ? [
          {
            text: t('button.edit'),
            icon: <EditIcon />,
            onClick: (item: Class) => {
              navigate({
                to: 'edit/$classId',
                params: {
                  classId: item.id
                }
              })
            }
          },
          {
            text: t('button.remove-class'),
            variantAction: 'danger' as const,
            icon: <DeleteIcon />,
            onClick: (item: Class) => {
              setOpenDeleteClassConfirmModal(true)
              setSelectedClassId(item.id)
            }
          }
        ]
      : [])
  ]

  const handleRemoveSuccess = () => {
    refetchClassess()
    toast({
      variant: 'success',
      title: t('toast.class-remove-success', {
        CLASS: classes?.list.find(c => c.id === selectedClassId)?.name
      })
    })
    setSelectedClassId(null)
  }

  const handleRemoveError = () => {
    setSelectedClassId(null)
    toast({
      variant: 'error',
      title: t('toast.class-remove-error', {
        CLASS: classes?.list.find(c => c.id === selectedClassId)?.name
      })
    })
  }

  const isSomeFilterSelected = filters.some(filter => filter.filterProps.value)

  const download = useClassesDownload({
    ...requestParams,
    columnsKey: manageColumns.activeColumnKeys
  })

  return (
    <>
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        actionsRender={
          <>
            <ExportButton {...download} />
            {user?.isSuperAdmin ? (
              <Button
                asLink
                to="/students-and-classes/classes/add"
                icon={<AddIcon />}
              >
                {t('button.add-new-class')}
              </Button>
            ) : null}
          </>
        }
        manageColumns={manageColumns}
      />
      <Table
        isLoading={isClassesLoading || isClassesCountLoading}
        id="classes-table"
        data={classes?.list || []}
        columns={columns}
        count={classes?.count}
        totalCount={classesCount}
        {...pagination}
        actions={actions}
        caption={t('header.classes')}
      />
      {selectedClassId ? (
        <RemoveClassModal
          open={openDeleteClassConfirmModal}
          onOpenChange={setOpenDeleteClassConfirmModal}
          classId={selectedClassId}
          onRemoveSuccess={handleRemoveSuccess}
          onRemoveError={handleRemoveError}
        />
      ) : null}
    </>
  )
}

export default ClassesView
